import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { calculateAICosts, getHistoricalMetrics } from '../../../services/metricsCalculationService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const PHASE_LABELS = {
  triage: 'Phase 1 — Triage',
  clarification_rounds: 'Clarification Rounds',
  phase3_vision: 'Phase 3 — Vision',
  annotation: 'Phase 3 — Annotations',
  report_generation: 'Phase 3 — Report',
};

const PHASE_COLORS = {
  triage: '#3b82f6',
  clarification_rounds: '#8b5cf6',
  phase3_vision: '#f59e0b',
  annotation: '#10b981',
  report_generation: '#ef4444',
};

const MODEL_COLORS = {
  'gpt-4o': '#3b82f6',
  'gpt-5.2': '#8b5cf6',
  'unknown': '#9ca3af',
};

const formatUSD = (val, decimals = 2) => {
  if (val == null || isNaN(val)) return '$0.00';
  if (val < 0.01 && val > 0) return `$${val.toFixed(4)}`;
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

const formatTokens = (val) => {
  if (!val) return '0';
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return val.toLocaleString();
};

const PRESETS = [
  { label: 'Last 24h', key: '1d', days: 1 },
  { label: 'Last 7 days', key: '7d', days: 7 },
  { label: 'Last 30 days', key: '30d', days: 30 },
  { label: 'Custom', key: 'custom', days: null },
];

const CostTrackingChart = () => {
  const [costs, setCosts] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [topCases, setTopCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customError, setCustomError] = useState('');

  useEffect(() => {
    if (preset !== 'custom') loadAllData();
  }, [preset]);

  const getDateRange = () => {
    if (preset === 'custom') {
      return {
        startIso: customStart ? new Date(customStart).toISOString() : null,
        endIso: customEnd ? new Date(customEnd + 'T23:59:59').toISOString() : null,
      };
    }
    const days = PRESETS.find(p => p.key === preset)?.days || 30;
    return {
      startIso: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(),
      endIso: null,
    };
  };

  const handleCustomApply = () => {
    setCustomError('');
    if (!customStart) { setCustomError('Please choose a start date.'); return; }
    if (customEnd && customEnd < customStart) { setCustomError('End date must be after start date.'); return; }
    loadAllData();
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      const { startIso, endIso } = getDateRange();
      const [costResult, histResult] = await Promise.all([
        calculateAICosts(startIso, endIso),
        getHistoricalMetrics(30, startIso, endIso)
      ]);

      if (costResult?.success) {
        setCosts(costResult.data);

        // Enrich top cases with case numbers
        if (costResult.data?.topCases?.length > 0) {
          const caseIds = costResult.data.topCases.map(c => c.caseId);
          const { data: caseDetails } = await supabase
            ?.from('cases')
            ?.select('id, case_number, property_address, status, persona')
            ?.in('id', caseIds);

          const caseMap = {};
          (caseDetails || []).forEach(c => { caseMap[c.id] = c; });

          setTopCases(costResult.data.topCases.map(c => ({
            ...c,
            ...(caseMap[c.caseId] || {})
          })));
        }
      }

      if (histResult?.success) {
        setHistoricalData(histResult.data || []);
      }
    } catch (error) {
      console.error('Load cost data error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived chart data ──────────────────────────────────────────
  const phaseChartData = useMemo(() => {
    if (!costs?.phaseBreakdown) return [];
    return Object.entries(costs.phaseBreakdown)
      .map(([phase, data]) => ({
        name: PHASE_LABELS[phase] || phase,
        cost: data.cost,
        tokens: data.tokens,
        runs: data.runs,
        fill: PHASE_COLORS[phase] || '#9ca3af',
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [costs]);

  const modelChartData = useMemo(() => {
    if (!costs?.modelBreakdown) return [];
    return Object.entries(costs.modelBreakdown)
      .map(([model, data]) => ({
        name: model,
        cost: data.cost,
        tokens: data.tokens,
        runs: data.runs,
        fill: MODEL_COLORS[model] || '#9ca3af',
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [costs]);

  const dailyTrendData = useMemo(() => {
    if (!costs?.dailyCosts) return [];
    return Object.entries(costs.dailyCosts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, cost]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cost: parseFloat(cost.toFixed(4)),
      }));
  }, [costs]);

  const historicalChartData = useMemo(() => {
    if (!historicalData?.length) return [];
    return historicalData.map(m => ({
      date: new Date(m.metric_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      revenue: parseFloat(m.net_revenue || 0),
      aiCosts: parseFloat(m.total_ai_cost || 0),
      stripeFees: parseFloat(m.stripe_fees || 0),
    }));
  }, [historicalData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-md">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Token') ? formatTokens(entry.value) : formatUSD(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!costs) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={48} className="mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Failed to load cost data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ─── Date Filter Bar ───────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground">Time range:</span>
          <div className="flex items-center gap-2">
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  preset === p.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/70'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {preset === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
              <input
                type="date"
                value={customStart}
                max={customEnd || new Date().toISOString().split('T')[0]}
                onChange={e => setCustomStart(e.target.value)}
                className="px-3 py-1.5 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={new Date().toISOString().split('T')[0]}
                onChange={e => setCustomEnd(e.target.value)}
                className="px-3 py-1.5 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={handleCustomApply}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Apply
              </button>
              {customError && (
                <span className="text-xs text-destructive">{customError}</span>
              )}
            </div>
          )}

          {loading && (
            <div className="ml-auto animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <SummaryCard
          icon="DollarSign"
          label="Total AI Cost"
          value={formatUSD(costs.totalAICost)}
          sub="Last 30 days"
          color="text-primary"
          bgColor="bg-primary/10"
        />
        <SummaryCard
          icon="FileText"
          label="Per Case (Avg)"
          value={formatUSD(costs.avgCost)}
          sub={`${costs.uniqueCases} cases`}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <SummaryCard
          icon="TrendingUp"
          label="P95 / Case"
          value={formatUSD(costs.p95Cost)}
          sub={`Max: ${formatUSD(costs.maxCost)}`}
          color="text-amber-600"
          bgColor="bg-amber-50"
        />
        <SummaryCard
          icon="Cpu"
          label="Total Tokens"
          value={formatTokens(costs.totalTokens)}
          sub={`${formatTokens(costs.totalPromptTokens)} in / ${formatTokens(costs.totalCompletionTokens)} out`}
          color="text-purple-600"
          bgColor="bg-purple-50"
        />
        <SummaryCard
          icon="Zap"
          label="AI Runs"
          value={costs.totalRuns?.toLocaleString()}
          sub={`${costs.uniqueCases} unique cases`}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <SummaryCard
          icon="BarChart2"
          label="Median / Case"
          value={formatUSD(costs.medianCost)}
          sub={`P99: ${formatUSD(costs.p99Cost)}`}
          color="text-teal-600"
          bgColor="bg-teal-50"
        />
      </div>

      {/* ─── Phase & Model Breakdown ───────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Phase Breakdown */}
        <div className="bg-card border border-border rounded-lg p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-1">Cost by Pipeline Phase</h3>
          <p className="text-sm text-muted-foreground mb-4">Where your AI budget goes</p>

          {phaseChartData.length > 0 ? (
            <>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={phaseChartData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis type="number" tickFormatter={(v) => formatUSD(v)} stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                    <YAxis dataKey="name" type="category" width={160} stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="cost" name="Cost" radius={[0, 4, 4, 0]}>
                      {phaseChartData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {phaseChartData.map((phase, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: phase.fill }} />
                      <span className="text-foreground">{phase.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-muted-foreground">{phase.runs} runs</span>
                      <span className="text-muted-foreground">{formatTokens(phase.tokens)} tokens</span>
                      <span className="font-medium text-foreground">{formatUSD(phase.cost)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState message="No phase data available" />
          )}
        </div>

        {/* Model Breakdown */}
        <div className="bg-card border border-border rounded-lg p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-1">Cost by Model</h3>
          <p className="text-sm text-muted-foreground mb-4">Token spend across AI models</p>

          {modelChartData.length > 0 ? (
            <>
              <div className="w-full h-64 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={modelChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={4}
                      dataKey="cost"
                      nameKey="name"
                    >
                      {modelChartData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {modelChartData.map((model, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: model.fill }} />
                      <span className="font-medium text-foreground">{model.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-muted-foreground">{model.runs} runs</span>
                      <span className="text-muted-foreground">{formatTokens(model.tokens)} tok</span>
                      <span className="font-medium text-foreground">{formatUSD(model.cost)}</span>
                      <span className="text-xs text-muted-foreground">
                        ({costs.totalAICost > 0 ? ((model.cost / costs.totalAICost) * 100).toFixed(1) : 0}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState message="No model data available" />
          )}
        </div>
      </div>

      {/* ─── Daily Cost Trend ──────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-1">Daily AI Spend</h3>
        <p className="text-sm text-muted-foreground mb-4">Cost per day over the last 30 days</p>

        {dailyTrendData.length > 0 ? (
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyTrendData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <YAxis tickFormatter={(v) => formatUSD(v)} stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="cost"
                  name="AI Cost"
                  stroke="var(--color-primary)"
                  fill="url(#costGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState message="No daily data yet — costs appear after cases are processed" />
        )}
      </div>

      {/* ─── Revenue vs Cost Historical ────────────────────────────── */}
      {historicalChartData.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-1">Revenue vs AI Costs</h3>
          <p className="text-sm text-muted-foreground mb-4">Historical daily breakdown from aggregated metrics</p>

          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historicalChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <YAxis tickFormatter={(v) => `$${v}`} stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" />
                <Bar dataKey="revenue" fill="var(--color-success)" name="Revenue" radius={[4, 4, 0, 0]} />
                <Bar dataKey="aiCosts" fill="var(--color-primary)" name="AI Costs" radius={[4, 4, 0, 0]} />
                <Bar dataKey="stripeFees" fill="var(--color-secondary)" name="Stripe Fees" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ─── Per-Case Cost Distribution ────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-1">Cost Distribution per Case</h3>
        <p className="text-sm text-muted-foreground mb-4">Statistical breakdown of per-case AI costs</p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatBox label="Average" value={formatUSD(costs.avgCost)} />
          <StatBox label="Median" value={formatUSD(costs.medianCost)} />
          <StatBox label="P95" value={formatUSD(costs.p95Cost)} />
          <StatBox label="P99" value={formatUSD(costs.p99Cost)} />
          <StatBox label="Maximum" value={formatUSD(costs.maxCost)} highlight />
        </div>

        {/* Top Expensive Cases Table */}
        {topCases.length > 0 && (
          <>
            <h4 className="text-sm font-semibold text-foreground mb-3">Top 10 Most Expensive Cases</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Case</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Location</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Persona</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Status</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">AI Runs</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Tokens</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {topCases.map((c, idx) => (
                    <tr key={c.caseId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-2 px-3 font-mono text-xs text-foreground">
                        {c.case_number || c.caseId?.substring(0, 8) + '…'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground truncate max-w-[200px]">
                        {c.property_address || '—'}
                      </td>
                      <td className="py-2 px-3">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                          {c.persona?.replace(/-/g, ' ') || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="py-2 px-3 text-right text-muted-foreground">{c.runs}</td>
                      <td className="py-2 px-3 text-right text-muted-foreground">{formatTokens(c.tokens)}</td>
                      <td className="py-2 px-3 text-right font-medium text-foreground">
                        {formatUSD(c.cost)}
                        {idx === 0 && c.cost > (costs.avgCost * 3) && (
                          <Icon name="AlertTriangle" size={14} className="inline ml-1 text-amber-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ─── Token Breakdown ───────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-1">Token Usage</h3>
        <p className="text-sm text-muted-foreground mb-4">Input vs output tokens across the pipeline</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">Prompt (Input) Tokens</p>
            <p className="text-2xl font-semibold text-blue-600">{formatTokens(costs.totalPromptTokens)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {costs.totalTokens > 0 ? ((costs.totalPromptTokens / costs.totalTokens) * 100).toFixed(1) : 0}% of total
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">Completion (Output) Tokens</p>
            <p className="text-2xl font-semibold text-purple-600">{formatTokens(costs.totalCompletionTokens)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {costs.totalTokens > 0 ? ((costs.totalCompletionTokens / costs.totalTokens) * 100).toFixed(1) : 0}% of total
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">Avg Tokens / Run</p>
            <p className="text-2xl font-semibold text-foreground">
              {costs.totalRuns > 0 ? formatTokens(Math.round(costs.totalTokens / costs.totalRuns)) : '0'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              across {costs.totalRuns} total runs
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────

function SummaryCard({ icon, label, value, sub, color, bgColor }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${bgColor}`}>
          <Icon name={icon} size={18} className={color} />
        </div>
      </div>
      <p className="text-xl font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 text-center ${highlight ? 'bg-destructive/10 border border-destructive/20' : 'bg-muted/50'}`}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-lg font-semibold ${highlight ? 'text-destructive' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    report_ready: 'bg-green-100 text-green-800',
    report_generating: 'bg-blue-100 text-blue-800',
    needs_clarification: 'bg-amber-100 text-amber-800',
    intake_submitted: 'bg-gray-100 text-gray-800',
    draft: 'bg-gray-50 text-gray-600',
    archived: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ') || '—'}
    </span>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Icon name="BarChart3" size={40} className="text-muted-foreground mb-3" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

export default CostTrackingChart;